import { ReactElement, useCallback } from 'react';
import useWindowWidth from '@app/common/useWindowWidth';
import useOrdersFilters from './useOrdersFilters';
import useStats from './useStats';
import Lang from '@lang/en';
import {
  OnChangeSearchTextType,
  OnSetFilterDateType,
  OrdersStateTypes,
} from '../ordersTable/types';
import FilterBarDesktop from './filterBarDesktop';
import FilterBarMobile from './filterBarMobile';
import { NullableString, SelectOption } from '@app/common/types';
import { TestCategory, TestType } from '../order/types';
import { StatsI } from './types';

const typeFilterOptions: Array<SelectOption> = [
  {
    value: ' ',
    label: 'All',
  },
  {
    value: 'antigen day_2',
    label: Lang.healthCheckAntigenTestLabelD2,
  },
  {
    value: 'antigen fit_to_fly_self',
    label: Lang.healthCheckAntigenTestLabelSelfFTF,
  },
  {
    value: 'antigen fit_to_fly_clinician',
    label: Lang.healthCheckAntigenTestLabelClinicianFTF,
  },
  {
    value: 'rt_pcr day_2',
    label: Lang.healthCheckPCRTestLabelD2,
  },
  {
    value: 'rt_pcr day_5',
    label: Lang.healthCheckPCRTestLabelD5,
  },
  {
    value: 'rt_pcr day_8',
    label: Lang.healthCheckPCRTestLabelD8,
  },
  {
    value: 'rt_pcr fit_to_fly_self',
    label: Lang.healthCheckPCRTestLabelSelfFTF,
  },
  {
    value: 'rt_pcr fit_to_fly_clinician',
    label: Lang.healthCheckPCRTestLabelClinicianFTF,
  },
];

export type FilterBarComponentI = {
  stats: StatsI;
  selectedStartDate: NullableString;
  ordersState?: string;
  isActive: (ordersType: OrdersStateTypes) => boolean;
  searchText?: string;
  onChangeSearchText: OnChangeSearchTextType;
  onSetFilterDate: OnSetFilterDateType;
  ordersStateChange: (ordersState: OrdersStateTypes) => void;
  handleChangeTestFilter: (value: string) => void;
  typeFilterOptions: Array<SelectOption>;
  currentTestFilterOption: SelectOption;
};

export const FilterBarComponent = (
  props: FilterBarComponentI
): ReactElement => {
  const { isMobileView } = useWindowWidth();

  return isMobileView ? (
    <FilterBarMobile {...props} />
  ) : (
    <FilterBarDesktop {...props} />
  );
};

const FilterBar = (): ReactElement => {
  const {
    filters,
    ordersStateChange,
    onChangeSearchText,
    onSetFilterDate,
    onChangeTestFilter,
  } = useOrdersFilters();
  const currentOrdersState = filters.ordersState;
  const selectedStartDate = filters.startDate;
  const filterOptionValue = `${filters.type} ${filters.category}` as const;
  const currentTestFilterOption =
    typeFilterOptions.find((option) => option.value === filterOptionValue) ||
    typeFilterOptions[0];

  const { stats } = useStats();

  const isActive = useCallback(
    (ordersType: OrdersStateTypes) => currentOrdersState === ordersType,
    [currentOrdersState]
  );

  const handleChangeTestFilter = (value: string): void => {
    const splitValue = value.split(' ');
    const type = splitValue[0] as TestType;
    const category = splitValue[1] as TestCategory;
    onChangeTestFilter(type, category);
  };

  const propsToPass = {
    stats,
    selectedStartDate,
    isActive,
    searchText: filters.searchText,
    ordersState: filters.ordersState,
    onChangeSearchText,
    ordersStateChange,
    onSetFilterDate,
    handleChangeTestFilter,
    currentTestFilterOption,
    typeFilterOptions,
  };

  return <FilterBarComponent {...propsToPass} />;
};

export default FilterBar;
